/**
 * Remembers that a desktop applicant opted out of the phone handoff and is
 * capturing in this tab instead.
 *
 * Why this is persisted at all: the choice covers the WHOLE remaining flow (ID
 * front → ID back → liveness), and those steps span several minutes of camera
 * work. React state alone would survive step transitions but not a refresh, so
 * one stray reload mid-capture would bounce the applicant back to a QR screen
 * they already declined.
 *
 * Why `sessionStorage` and not `localStorage`: the preference belongs to ONE
 * applicant's sitting, not to the machine. Verification desktops are routinely
 * shared (branch counters, kiosks, a relative's laptop), and `localStorage`
 * would silently carry one applicant's choice into the next applicant's flow.
 * `sessionStorage` dies with the tab, which is exactly the intended lifetime.
 *
 * Keyed by flow id so two different cases opened in the same tab can't inherit
 * each other's choice.
 *
 * Every access is wrapped: Safari private mode and hardened enterprise profiles
 * throw on `sessionStorage` access rather than returning null. A storage failure
 * must degrade to "applicant sees the QR again after a refresh", never to a
 * thrown render.
 */

const KEY_PREFIX = 'trustsy.captureHere:';

/** Storage key for a flow's capture-device preference. Exported for tests. */
export function captureHereKey(flowId: string): string {
  return `${KEY_PREFIX}${flowId}`;
}

/** True when this flow was already switched to in-tab capture. */
export function prefersCaptureHere(flowId: string): boolean {
  if (!flowId) return false;
  try {
    return window.sessionStorage.getItem(captureHereKey(flowId)) === '1';
  } catch {
    return false;
  }
}

/** Record that this flow should keep capturing in this tab. */
export function rememberCaptureHere(flowId: string): void {
  if (!flowId) return;
  try {
    window.sessionStorage.setItem(captureHereKey(flowId), '1');
  } catch {
    // Storage unavailable — the in-memory state still carries the choice for
    // this page life, which is the common case. Nothing to recover here.
  }
}

/**
 * Undo the preference and send the applicant back to the phone handoff.
 *
 * Clearing STORAGE as well as component state is the whole point: this runs when
 * the in-tab camera turned out not to work (no webcam, permission refused), and
 * a stored `1` would silently re-select the broken path on the next refresh —
 * trapping them in a loop they explicitly just escaped.
 */
export function forgetCaptureHere(flowId: string): void {
  if (!flowId) return;
  try {
    window.sessionStorage.removeItem(captureHereKey(flowId));
  } catch {
    // Same degradation as the write path.
  }
}
